import { OpeningHours } from 'lib/types';
import {
    DayOfTheWeek,
    DAYS_OF_THE_WEEK_FROM_MONDAY as DAYS_OF_THE_WEEK,
} from '@bondvet/types/common';
import isEqual from 'lodash/isEqual';
import { formatTime } from 'lib/util';
import { CompoundLocationHours } from './types';

const getDayLabel = (day: DayOfTheWeek) => {
    return day[0].toUpperCase() + day.substring(1);
};

export const mapOpeningHours = (
    weekHours: Partial<OpeningHours>,
): ReadonlyArray<{ days: string; hours: string }> => {
    if (weekHours) {
        return DAYS_OF_THE_WEEK.map((day) => ({ day, hours: weekHours[day] }))
            .reduce((acc: CompoundLocationHours[], { day, hours }) => {
                if (acc.length === 0) {
                    return [{ startDay: day, endDay: day, hours }];
                }
                if (isEqual(hours, acc[acc.length - 1].hours)) {
                    acc[acc.length - 1].endDay = day;
                    return acc;
                }
                return [
                    ...acc,
                    {
                        startDay: day,
                        endDay: day,
                        hours,
                    },
                ];
            }, [])
            .map(({ startDay, endDay, hours }) => ({
                days:
                    startDay === endDay
                        ? getDayLabel(startDay)
                        : `${getDayLabel(startDay)} - ${getDayLabel(endDay)}`,
                hours: hours
                    ? hours
                          .map(
                              ({ start, end }) =>
                                  `${formatTime(start)} - ${formatTime(end)}`,
                          )
                          .join(', ')
                    : 'Closed',
            }));
    }
    return [];
};

export type OpeningHoursBlock = {
    start: string;
    end: string;
};

export type LocationHours = {
    sunday?: ReadonlyArray<OpeningHoursBlock>;
    monday?: ReadonlyArray<OpeningHoursBlock>;
    tuesday?: ReadonlyArray<OpeningHoursBlock>;
    wednesday?: ReadonlyArray<OpeningHoursBlock>;
    thursday?: ReadonlyArray<OpeningHoursBlock>;
    friday?: ReadonlyArray<OpeningHoursBlock>;
    saturday?: ReadonlyArray<OpeningHoursBlock>;
};

export function formatHours(locationHours: LocationHours): string[] {
    const days: Array<keyof LocationHours> = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
    ];
    const shortDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    let sameHoursDays: string[] = [];
    let currentHours: OpeningHoursBlock | null = null;

    const openingHours: string[] = [];

    for (let i = 0; i < days.length; i++) {
        const day = days[i];
        const hours = locationHours[day];
        if (hours && hours.length > 0) {
            if (!currentHours) {
                sameHoursDays.push(shortDays[i]);
                currentHours = hours[0];
            } else if (
                currentHours.start !== hours[0].start ||
                currentHours.end !== hours[0].end
            ) {
                const dayString = sameHoursDays.join(', ');
                openingHours.push(
                    `${dayString} ${currentHours.start}-${currentHours.end}`,
                );
                sameHoursDays = [shortDays[i]];
                currentHours = hours[0];
            } else {
                sameHoursDays.push(shortDays[i]);
            }
        }
    }

    // Push the last group of hours to the array
    if (sameHoursDays.length > 0 && currentHours) {
        const dayString = sameHoursDays.join(', ');
        openingHours.push(
            `${dayString} ${currentHours.start}-${currentHours.end}`,
        );
    }

    return openingHours;
}
